import React, { useState, useEffect } from 'react';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { Link } from 'react-router-dom';
import { Gift, TrendingUp, Users, ArrowRight, Share2, Trophy } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function TopAmbassadeursPage() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND}/api/parrainage/leaderboard?limit=10`).then(r => r.json()),
      fetch(`${BACKEND}/api/admin/parrainage/cash-dashboard`).then(r => r.json()).catch(() => null),
    ]).then(([lb, stats]) => {
      setLeaderboard(lb.leaderboard || []);
      if (stats) setAdminStats(stats);
    }).catch(() => {}).finally(() => setLoading(false));
  // eslint-disable-next-line
  }, []);

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      {/* Hero */}
      <div className="relative overflow-hidden" style={{ minHeight: 260 }}>
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(93,188,191,0.10) 50%, transparent 100%)'
        }} />
        <div className="container-ose relative z-10" style={{ paddingTop: '3rem', paddingBottom: '2.5rem' }}>
          <div className="flex items-center gap-4 mb-4">
            <span style={{ fontSize: 44 }}>🏆</span>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#D4AF37' }}>Programme Ambassadeur</p>
              <h1 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
                Top Ambassadeurs
              </h1>
            </div>
          </div>
          <p className="text-sm max-w-lg" style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>
            Les ambassadeurs qui recommandent le plus et gagnent le plus chaque mois.
            Partagez votre lien et rejoignez le classement !
          </p>

          {/* Stats globales */}
          {adminStats && (
            <div className="flex flex-wrap gap-4 mt-5">
              {[
                { label: 'CA généré', value: `${adminStats.ca_genere?.total?.toFixed(0)}€`, color: '#1F9D7A' },
                { label: 'Commissions versées', value: `${adminStats.commissions?.total_verse?.toFixed(0)}€`, color: '#D4AF37' },
                { label: 'ROI programme', value: adminStats.roi_programme, color: '#5DBCBF' },
              ].map((s, i) => (
                <div key={`stat-${i}`} className="px-4 py-2.5 rounded-xl" style={{ background: `${s.color}10`, border: `1px solid ${s.color}25` }}>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{s.label}</p>
                  <p className="text-lg font-bold font-mono-ose" style={{ color: s.color }}>{s.value}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="container-ose" style={{ paddingBottom: '5rem' }}>
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin w-10 h-10 rounded-full" style={{ border: '2px solid var(--border)', borderTopColor: '#D4AF37' }} />
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="text-center py-20">
            <Trophy size={56} className="mx-auto mb-5" style={{ color: 'var(--border)' }} />
            <h2 className="font-display text-xl font-bold mb-3" style={{ color: 'var(--ink)' }}>Le classement se remplit...</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>Soyez le premier ! Partagez votre lien et gagnez 5% sur chaque commande.</p>
            <Link to="/mon-compte" className="btn-teal px-7 py-3.5 rounded-xl text-sm font-bold inline-flex items-center gap-2">
              <Gift size={16} /> Mon lien ambassadeur
            </Link>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto">
            <div className="space-y-3 mb-8">
              {leaderboard.map((entry, i) => (
                <div key={`leader-${i}`}
                  className="flex items-center gap-4 p-4 rounded-2xl"
                  style={{
                    background: i === 0 ? 'linear-gradient(135deg, rgba(212,175,55,0.12), rgba(212,175,55,0.04))' : '#FFFFFF',
                    border: `1.5px solid ${i === 0 ? 'rgba(212,175,55,0.4)' : i === 1 ? 'rgba(192,192,192,0.4)' : i === 2 ? 'rgba(205,127,50,0.3)' : 'var(--border)'}`,
                    boxShadow: i === 0 ? '0 4px 20px rgba(212,175,55,0.12)' : 'none',
                  }}
                >
                  {/* Rang */}
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: i === 0 ? 'rgba(212,175,55,0.15)' : 'var(--bg)' }}>
                    <span style={{ fontSize: 20 }}>{entry.badge}</span>
                  </div>
                  {/* Rang numérique */}
                  <div className="w-6 text-center flex-shrink-0">
                    <p className="font-bold text-sm" style={{ color: i < 3 ? '#D4AF37' : 'var(--text-muted)' }}>#{entry.rank}</p>
                  </div>
                  {/* Nom */}
                  <p className="flex-1 font-semibold text-sm" style={{ color: 'var(--ink)' }}>{entry.prenom}</p>
                  {/* Gains */}
                  <p className="price-gold font-bold text-base">{entry.total_earned.toFixed(2)}€</p>
                </div>
              ))}
            </div>

            {/* CTA rejoindre */}
            <div className="p-6 rounded-2xl text-center"
              style={{ background: 'linear-gradient(135deg, rgba(93,188,191,0.1), rgba(212,175,55,0.08))', border: '1px solid rgba(93,188,191,0.2)' }}>
              <p className="font-bold text-base mb-2" style={{ color: 'var(--ink)' }}>Rejoignez le classement</p>
              <p className="text-sm mb-5" style={{ color: 'var(--text-muted)', lineHeight: 1.6 }}>
                Partagez votre lien et gagnez jusqu'à <strong style={{ color: 'var(--teal)' }}>15%</strong> sur chaque commande générée.
                +5€ bonus sur votre premier parrainage validé.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/mon-compte"
                  className="btn-teal px-6 py-3 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-2">
                  <Gift size={15} /> Mon lien ambassadeur
                </Link>
                <Link to="/mes-evenements"
                  className="px-6 py-3 rounded-xl text-sm font-semibold inline-flex items-center justify-center gap-2"
                  style={{ background: '#FFFFFF', color: 'var(--ink)', border: '1px solid var(--border)' }}>
                  Créer un événement <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
